<template>
  <div class="p-2 advanced-table-wrapper">
    <div class="table-area">
      <div id="catalog-table-wrapper" class="table-wrapper table-scroll" :style="[{ ...columnSizeVars() }]">
        <table v-if="getRowsCount > 0" class="advanced-table" :class="{ noVariants: !hasClickedProductVariants }">
          <thead>
            <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :style="[{ ...getCommonPinningStyles(header) }]"
                :class="[
                  { pinned: header.column.getIsPinned() },
                  { listPrice: header.column.columnDef.accessorKey === 'listPrice' }
                ]"
                :colSpan="header.colSpan">
                <FlexRender v-if="!header.isPlaceholder" :render="header.column.columnDef.header" :props="header.getContext()" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in visibleRows" :key="row.id" :class="[{ selected: row.getIsSelected() }, { disabled: !row.getCanSelect() }]">
              <td v-for="cell in row.getVisibleCells()" :key="cell.id" :style="{ ...getCommonPinningStyles(cell) }">
                  <FlexRender
                    :render="cell.column.columnDef.cell"
                    :props="cell.getContext()"
                  />
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="getRowsCount === 0" class="empty-table">
          <img src="@/assets/images/empty-catalog-table.svg" alt="Ürün bulunamadı" />
          <span class="title">
            Ürünün varyantlarını seçtikten sonra<br />
            bilgilerini girip hemen satışa açabilirsiniz.
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FlexRender, getCoreRowModel, useVueTable } from '@tanstack/vue-table'
import { Button, NativeSelect } from '@n11/so-ui-components';
import { ref, computed, reactive } from 'vue'
import { useStore } from 'vuex';
import BasicInput from "@/components/DataTable/BasicInput"

export default {
  components: {
    FlexRender,
    Button,
    NativeSelect,
    BasicInput
  },
  props: {
    variants: {
      type: Array,
      required: true,
      default: []
    },
    columns: {
      type: Array,
      required: true,
    },
    hasClickedProductVariants: {
      type: Boolean,
      required: false,
      default: true,
    }
  },
  setup(props) {
    const table = ref(null);
    const data = ref([]);
    const store = useStore();
    const itemErrors = reactive({});

    const columnPinning = ref({
      left: ['select-col', 'name'],
      right: ['vatRate', 'actions'],
    });

    const { columns } = props
    
    const visibleRows = computed(() => {
      if (table.value) {

        // if no selectable variant, adjust pinning
        if (!props.hasClickedProductVariants) {
          columnPinning.value = {};
        }

        return table.value.getRowModel().rows || [];
      }
      return [];
    })
  

    const getRowsCount = computed(() => data.value?.length)

    table.value = useVueTable({
      get data() {
        return data.value.length ? data.value : []
      },
      columns,
      initialState: {
        columnPinning,
      },
      getRowId: row => row.id,
      getCoreRowModel: getCoreRowModel(),
      meta: {
        addRow: (variant) => {
          let oldData = [];
          if (data.value) {
            oldData = [...data.value];
          }
          data.value = [...oldData, variant];
        },
        updateRow: (id, columnId, value) => {
          if (data.value) {
            const updatedData = data.value.map((row) => {
              if (row.id === id) {
                return {
                  ...row,
                  [columnId]: value,
                };
              }
              return row;
            });
            data.value = updatedData;
          }
        },
        removeRow: (rowId) => {
          data.value = [...data.value.filter(item => item.id !== rowId)];
        },
        getColumnDataExceptThis: (columnName, id) => {
          const columnData = data.value.map((row) => {
            return row.id === id ? null : row[columnName];
          });

          return columnData.filter((col) => col !== null);
        },
        getData: () => {
          return data.value;
        },
      }
    });

    const getCommonPinningStyles = (column) => {
      const isPinned = column.column.getIsPinned()
      const isLastLeftPinnedColumn = isPinned === 'left' && column.column.getIsLastColumn('left')
      const isFirstRightPinnedColumn = isPinned === 'right' && column.column.getIsFirstColumn('right')

      return {
        boxShadow: isLastLeftPinnedColumn
          ? '-4px 0 4px -4px gray inset'
          : isFirstRightPinnedColumn
            ? '4px 0 4px -4px gray inset'
            : undefined,
        left: isPinned === 'left' ? `${column.column.getStart('left')}px` : undefined,
        right: isPinned === 'right' ? `${column.column.getAfter('right')}px` : undefined,
        opacity: isPinned ? 0.95 : 1,
        position: isPinned ? 'sticky' : 'relative',
        width: column.column.getSize(),
        zIndex: isPinned ? 1 : "unset",
        minWidth: `calc(var(--col-${column.column.id}-size) * 1px)`,
        maxWidth: !isPinned ? `calc(var(--col-${column.column.id}-size) * 1px)` : 'auto',
      }
    }

    const columnSizeVars = () => {
      const headers = table.value.getFlatHeaders()
      const colSizes = {}

      for (let i = 0; i < headers.length; i++) {
        const header = headers[i]
        colSizes[`--header-${header.id}-size`] = header.getSize()
        colSizes[`--col-${header.column.id}-size`] = header.column.getSize()
      }

      return colSizes
    }

    const getSelectedRows = () => {
      return table.value.getState().rowSelection
    }

    return {
      table,
      columnSizeVars,
      getCommonPinningStyles,
      getSelectedRows,
      visibleRows,
      getRowsCount,
      itemErrors,   
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
@import '@/assets/styles/utilities.scss';

.advanced-table-wrapper {
  display: flex;
  gap: 24px;
  flex-direction: column;
  font-family: 'Open Sans';
  position: relative;
  .table-area {
    position: relative;
    z-index: 0;
    min-height: 291px;
  }
}

.table-wrapper {
  overflow-x: scroll;
  width: 100%;
  max-width: 1000px;
  position: relative;
  border: 1px solid $colorN50;
  border-radius: 8px;
  border-spacing: 0;
  border-collapse: separate;
  overflow-y: auto;
  overscroll-behavior: contain;
  max-height: 291px;

  &.table-scroll::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &.table-scroll::-webkit-scrollbar {
    width: 4px;
    height: 4px;
    background-color: transparent;
  }

  &.table-scroll::-webkit-scrollbar-thumb {
    background-color: $colorN60;
    border-radius: 8px;
  }

  .prices {
    max-height:18px !important;
    overflow: visible !important;
  }

  .empty-table {
    display: flex;
    padding: 24px 0;
    align-items: center;
    flex-direction: column;
    position: sticky;
    left: 0;

    img {
      max-width: 120px;
      margin-bottom: 16px;
    }

    >span {
      display: block;
      font-size: 20px;
      line-height: 26px;
      letter-spacing: -0.02px;
      text-align: center;
      color: $colorN80;
    }

    button {
      width: 193px;
      gap: 4px;

      img {
        margin: auto 0;
      }
    }
  }
}

.advanced-table {
  width: 100%;
  border-radius: 8px;
  border-collapse: separate;

  &.noVariants {
    thead tr th {
      &:nth-child(1), &:nth-child(2), &:last-child {
        display: none;
      }
    }

    tbody tr td {
      &:nth-child(1), &:nth-child(2), &:last-child {
        display: none;
      }
    }
  }

  thead {
    background: $colorD10;
    color: $colorN80;

    tr {
      height: 50px;
      
      
      th {
        padding: 8px 12px;
        text-align: left;
        vertical-align: middle;
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        letter-spacing: -0.05px;
        color: $colorN80;
        background-color: $colorBe20;
        border-bottom: 2px solid $colorN30;
        position: sticky !important;
        top: 0;
        z-index: 2 !important;

        &.listPrice {
          z-index: 3 !important;
        }
        

        &.pinned {
          z-index: 3 !important;
        }

        &:not(:last-child) {
          border-right: 1px solid $colorN50;
        }
      }
    }
  }

  tbody {
    tr {
      td {
        background-color: $colorN00;
        height: 60px;
        vertical-align: middle;

        > {
          div {
            margin: auto;
          }

          label {
            display: block;
            margin: 0 12px;
          }

          .product-info {
            width: 244px;
          }

          .popover-wrapper {
            padding: 0 12px;
          }

          .button-wrapper,
          .status-wrapper {
            margin: 0 12px;
          }

          .checkbox {
            display: flex;
            justify-content: center;
          }
        }

        &:not(:last-child) {
          border-right: 1px solid $colorN50;
        }
      }

      &.selected {
        td {
          background-color: $colorM05;
        }
      }

      &:not(:last-child) {
        td {
          border-bottom: 2px solid $colorN50;
        }
      }

      &:last-child {
        td :deep(.tooltip-text) {
          top: 60px !important;
        }
      }
    }
  }
}
</style>
